import type { BillingCurrency } from '@contracts/payment/Currency';
import type { PaymentMethod } from '@contracts/payment/PaymentMethod';
import AddIcon from '@mui/icons-material/Add';
import { ClientFormSection } from '@src/clients/ClientFormSection';
import type { Client } from '@src/clients/domain/Client';
import { BillingCurrencyDropdown } from '@src/clients/financialSettings/BillingCurrencyDropdown';
import { CreditCardForm } from '@src/clients/financialSettings/CreditCardForm';
import { QuickbooksClientMapping } from '@src/clients/financialSettings/QuickbooksClientMapping';
import { SavedCreditCards } from '@src/clients/financialSettings/SavedCreditCards';
import { ThirdPartyBillingSection } from '@src/clients/financialSettings/ThirdPartyBillingSection';
import { useListAllCreditCards } from '@src/clients/hooks/useListAllClientCreditCards';
import { useUpdateInvoicingInstructions } from '@src/clients/hooks/useUpdateInvoicingInstructions';
import { useUpdatePaymentMethod } from '@src/clients/hooks/useUpdatePaymentMethod';
import { useUpdateRequirePO } from '@src/clients/hooks/useUpdateRequirePO';
import { Accordion } from '@src/common/components/Accordion';
import { BaseModal } from '@src/common/components/BaseModal';
import { Button } from '@src/common/components/button/Button';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { FormRadioGroup } from '@src/common/components/FormRadioGroup';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { useDeleteCreditCard } from '@src/orders/hooks/useDeleteCreditCard';
import { useRefreshOrders } from '@src/orders/hooks/useRefreshOrders';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { useCallback, useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  client: Client;
  refreshData: () => void;
}

export const FinancialSettings = ({ client, refreshData }: Props) => {
  const { t } = useTranslation();
  const [openCreditCardForm, setOpenCreditCardForm] = useState(false);
  const { showToast } = useToast();
  const { refreshOrders } = useRefreshOrders();
  const [accordionState, setAccordionState] = useState({
    billingAndPayment: true,
  });

  const { deleteCreditCard } = useDeleteCreditCard({
    async onSuccess() {
      showToast({
        severity: ToastLevel.success,
        message: t('savedCreditCards.deleteModal.success'),
      });
      await refetchCreditCards();
    },
    onError() {
      showToast({
        severity: ToastLevel.error,
        message: t('savedCreditCards.deleteModal.error'),
      });
    },
  });

  const { control, handleSubmit, formState, reset, getValues, watch } = useForm(
    {
      defaultValues: {
        paymentMethod: client.paymentMethod,
        currency: client.currency,
        requirePO: client.requirePO,
        invoicingInstructions: client.invoicingInstructions ?? undefined,
      },
    },
  );

  const { updatePaymentMethod, isLoading: isUpdatingPaymentMethod } =
    useUpdatePaymentMethod({
      clientId: client.id,
      onSuccess: () => {
        showToast({
          severity: ToastLevel.success,
          message: t('financialSettingsChange.success'),
        });
        refreshData();
        refreshOrders();
        reset(getValues());
      },
      onError: () =>
        showToast({
          severity: ToastLevel.error,
          message: t('financialSettingsChange.error'),
        }),
    });

  const { updateRequirePO, isLoading: isUpdatingRequirePO } =
    useUpdateRequirePO({
      clientId: client.id,
      onSuccess: () => {
        showToast({
          severity: ToastLevel.success,
          message: t('requirePO.success'),
        });
        refreshData();
        reset(getValues());
      },
      onError: () =>
        showToast({
          severity: ToastLevel.error,
          message: t('requirePO.error'),
        }),
    });

  const {
    updateInvoicingInstructions,
    isLoading: isUpdatingInvoicingInstructions,
  } = useUpdateInvoicingInstructions({
    clientId: client.id,
    onSuccess: () => {
      showToast({
        severity: ToastLevel.success,
        message: t('invoicingInstructions.success'),
      });
      refreshData();
      reset(getValues());
    },
    onError: () =>
      showToast({
        severity: ToastLevel.error,
        message: t('invoicingInstructions.error'),
      }),
  });

  const { refetch: refetchCreditCards, availableCreditCards } =
    useListAllCreditCards({
      clientId: client.id,
    });

  const saveClientPaymentMethod: SubmitHandler<{
    paymentMethod?: PaymentMethod | 'NONE';
    currency?: BillingCurrency;
  }> = useCallback(
    ({ paymentMethod, currency }) => {
      if (paymentMethod === 'LINE_OF_CREDIT') {
        if (currency !== undefined) {
          updatePaymentMethod({
            paymentMethod: 'LINE_OF_CREDIT',
            currency,
          });
        }
      } else {
        updatePaymentMethod({
          paymentMethod: paymentMethod === 'NONE' ? undefined : paymentMethod,
        });
      }
    },
    [updatePaymentMethod],
  );

  const saveClientRequirePO: SubmitHandler<{
    requirePO: boolean | string;
  }> = useCallback(
    ({ requirePO }) => {
      updateRequirePO({
        requirePO: requirePO === 'true' || requirePO === true,
      });
    },
    [updateRequirePO],
  );

  const saveClientInvoicingInstructions: SubmitHandler<{
    invoicingInstructions?: string;
  }> = useCallback(
    ({ invoicingInstructions }) => {
      updateInvoicingInstructions({
        invoicingInstructions,
      });
    },
    [updateInvoicingInstructions],
  );

  const paymentMethod = watch('paymentMethod');
  const currency = watch('currency');
  const requirePO = watch('requirePO');
  useEffect(() => {
    if (
      formState.isDirty &&
      ['paymentMethod', 'currency'].some((key) => key in formState.dirtyFields)
    ) {
      handleSubmit(saveClientPaymentMethod)();
    }

    if (
      formState.isDirty &&
      ['requirePO'].some((key) => key in formState.dirtyFields)
    ) {
      handleSubmit(saveClientRequirePO)();
    }
  }, [
    paymentMethod,
    currency,
    requirePO,
    handleSubmit,
    saveClientPaymentMethod,
    saveClientRequirePO,
    formState.isDirty,
    formState.dirtyFields,
  ]);

  const handleInvoicingInstructionsBlur = useCallback(() => {
    if (formState.isDirty && 'invoicingInstructions' in formState.dirtyFields) {
      handleSubmit(saveClientInvoicingInstructions)();
    }
  }, [
    handleSubmit,
    saveClientInvoicingInstructions,
    formState.isDirty,
    formState.dirtyFields,
  ]);

  const onSaveCreditCardSuccess = () => {
    showToast({
      message: t('creditCardSave.success'),
      severity: ToastLevel.success,
    });
    setOpenCreditCardForm(false);
    void refetchCreditCards();
  };

  const onSaveCreditCardError = () =>
    showToast({
      message: t('creditCardSave.error'),
      severity: ToastLevel.error,
    });

  const onCreditCardSavedWithError = ({
    errorCode,
    description,
  }: {
    description: string;
    errorCode: string;
  }) => {
    showToast({
      message: t('creditCardSave.savedWithError', { errorCode, description }),
      severity: ToastLevel.warning,
      closesAutomatically: false,
    });
    refetchCreditCards();
  };

  return (
    <>
      <Accordion
        id="billingAndPayment"
        title={t('billingAndPayment')}
        expanded={accordionState.billingAndPayment}
        onChange={(_, expanded) =>
          setAccordionState({
            ...accordionState,
            billingAndPayment: expanded,
          })
        }
      >
        <form>
          <ClientFormSection title={t('defaultPaymentMethod')}>
            <Stack width="50%">
              <FormDropdown
                fieldName="paymentMethod"
                control={control}
                label={t('fields.paymentMethod')}
                options={[
                  {
                    label: t('paymentOptions.creditCard'),
                    value: 'CREDIT_CARD',
                  },
                  {
                    label: t('paymentOptions.lineOfCredit'),
                    value: 'LINE_OF_CREDIT',
                  },
                ]}
                disabled={isUpdatingPaymentMethod}
              />
              {paymentMethod === 'LINE_OF_CREDIT' && (
                <Stack marginTop={theme.spacing['3']}>
                  <BillingCurrencyDropdown control={control} />
                </Stack>
              )}
              <Stack
                marginTop={theme.spacing['3']}
                marginBottom={theme.spacing['6']}
              >
                <ControllerFormTextInput
                  control={control}
                  fieldName="invoicingInstructions"
                  label={t('invoicingInstructions.label')}
                  disabled={isUpdatingInvoicingInstructions}
                  multiline
                  onBlur={handleInvoicingInstructionsBlur}
                />
              </Stack>
            </Stack>
            <QuickbooksClientMapping
              clientId={client.id}
              quickbooksMapping={client.quickbooksMapping}
            />
            <Stack>
              <Stack direction="row" alignSelf="flex-end">
                {client.paymentMethod === 'CREDIT_CARD' && (
                  <Button
                    endIcon={<AddIcon />}
                    onClick={() => setOpenCreditCardForm(true)}
                  >
                    {t('creditCard')}
                  </Button>
                )}
              </Stack>
              <SavedCreditCards
                creditCards={availableCreditCards}
                clientId={client.id}
                onDeleteCreditCard={deleteCreditCard}
              />
            </Stack>

            <Stack
              marginBottom={theme.spacing['6']}
              marginTop={theme.spacing['3']}
            >
              <Typography
                component="h4"
                variant="body1"
                style={{ fontWeight: 500 }}
              >
                {t('requirePO.title')}
              </Typography>
              <Typography>{t('requirePO.subtitle')}</Typography>
              <FormRadioGroup
                control={control}
                fieldName="requirePO"
                disabled={isUpdatingRequirePO}
                options={[
                  { label: t('yes'), value: true },
                  { label: t('no'), value: false },
                ]}
              />
            </Stack>
          </ClientFormSection>

          <ThirdPartyBillingSection client={client} />
        </form>
      </Accordion>

      <BaseModal
        title={t('addCreditCard')}
        onClose={() => setOpenCreditCardForm(false)}
        open={openCreditCardForm}
      >
        <CreditCardForm
          clientId={client.id}
          onSuccess={onSaveCreditCardSuccess}
          onError={onSaveCreditCardError}
          onSavedWithError={onCreditCardSavedWithError}
        />
      </BaseModal>
    </>
  );
};
